import uniqid from 'uniqid';
import { translate } from '@shakkerai/util-i18n';
import { getUserinfo } from '@shakkerai/business-userinfo';
import { calculateHash } from '@shakkerai/ext-oss/src/hash';
import * as ossExts from '@shakkerai/ext-oss';
import { defaultHiResScale, samplerMethodOptions, vaeOptions, controlNetConfig } from '@shakkerai/pro-constants';
import { notifySuccess, notifyError, notifyWarn } from '@shakkerai/pro-components';
import {
  compressLocalImage,
  convert2CompressedUrl,
  convertRemoteImageToBase64,
  fitImageSize,
  getRemoteImageSize,
} from '@shakkerai/util-image';
import { getImageOssPath, getImageOssUrl } from '@shakkerai/util-image';
import {
  requestSegmentImage,
  requestTaskDetail,
  requestRevPromptV2,
  getImageParams,
  searchGalleryList,
  imageReview,
} from '@shakkerai/services';
import { getLoadings, getVipState, updateVipState, useSegments } from '@shakkerai/pro-store';
import {
  setSharedData,
  getSharedData,
  setTempState,
  setViewport,
  getViewport,
  getTempState,
  viewportActions,
} from '@starflow/app/store';
import { getNextName, getNexIndex } from './getNextName';
import { getSharedStage } from '@starflow/app/components/canvas/stage';
import { insertElements } from '@starflow/app/helpers/canvas';
import { findNextTaskPosition, LoraCodeReg } from '@starflow/app/modules/generator/helpers';
import { initialGenerateParams } from '@shakkerai/pro-store';
import { isMainland, inferenceAPIBucket, downloadCanvas } from '@shakkerai/pro-utils';
import { mc } from '@shakkerai/report';
import { submitTranslate } from '@shakkerai/services/src/canva';
import { requestRevStyle, updatePromptCache } from '@shakkerai/services/src/pro';
import { getElementGenInfo } from '@shakkerai/pro-hooks';
import { getProjectState } from '@starflow/app/store';
import { scheduleTask } from '@starflow/app/helpers/process';
import { getUnitData } from '@starflow/app/helpers/flow';
import { findInsertPosition } from '@starflow/app/helpers/element';
import { vipOptions } from '@starflow/app/modules/generator/components/_legacy/count';

const cloudStorageExts = ossExts;

const requestSegment = async (url, mode, externalParams) => {
  const { results } = await requestSegmentImage({
    images: [url],
    mode,
    ...inferenceAPIBucket,
    ...externalParams,
  }).catch(() => {
    return { results: [] };
  });

  return results;
};

const requestSegmentImageWithCache = async (url, scale = 1, mode = 1) => {
  const cacheKey = getImageSegmentCacheKey(url, mode === 1 ? 'inpaint' : 'changeFace');
  const isChangeFaceMode = mode === 8;

  const memoryCachedResult = getSharedData(cacheKey);

  if (memoryCachedResult) {
    return memoryCachedResult;
  }

  let externalParams = {};
  // change face mode
  if (isChangeFaceMode) {
    Object.assign(externalParams, {
      text: 'face',
      boxThreshold: 0.3,
    });
  }

  try {
    let results = await requestSegment(url, mode, externalParams);

    // if (isChangeFaceMode && results.length > 0) {
    //   // 换脸模式下，需要把模式4的内容再请求模式3
    //   results = requestSegment(
    //     url,
    //     3,
    //     Object.assign(externalParams, {
    //       boxes: results[0].boxes,
    //     }),
    //   );
    // }

    const shapes = results[0]
      .map((item) => {
        return { points: item.contours.map((item) => [item[0][0] * scale, item[0][1] * scale]) };
      })
      .map((item) => {
        return {
          points: item.points,
          area: calcShapeArea(item),
        };
      })
      .sort((a, b) => b.area - a.area);

    if (shapes.length) {
      setSharedData(cacheKey, shapes);
    }

    return shapes;
  } catch {
    // noop
  }

  return null;
};

export const MAX_UPLOAD_SIZE = 2048;
export const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const ACCEPTED_FILE_EXTS = ['png', 'jpg', 'jpeg', 'webp'];

let taskDetailCache = {};

export const insertHistoryImage = async ({ generateId, outputId, url, x, y }) => {
  let taskDetail = taskDetailCache[generateId];

  const id = `element-image-${uniqid()}`;

  try {
    const { width, height } = await getRemoteImageSize(url);

    // 再获取和更新图片信息
    if (!taskDetail) {
      taskDetail = await requestTaskDetail({ generateId }).then((res) => res.data);
      taskDetailCache[id] = taskDetail;
    }

    if (!taskDetail) {
      return insertImage({ id, url, x, y, width, height });
    }

    const { taskParam, images } = taskDetail;
    const image = images.find((image) => image.outputId === outputId);

    if (!image) {
      return insertImage({ id, url, x, y, width, height });
    }

    insertImage({
      ...image,
      id,
      url,
      x,
      y,
      width,
      height,
      innerImageId: outputId,
      innerTaskId: generateId,
    });

    setTimeout(() => {
      viewportActions.locateElement(id, { animate: true, keepScale: true });
    }, 10);
  } catch (error) {
    console.error(error);
  }
};

export const getCanvasCenter = () => {
  const viewport = getViewport();

  const { width: canvasWidth, height: canvasHeight } = document.body.getBoundingClientRect();
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  let x = centerX / viewport.scale - viewport.x / viewport.scale;
  let y = centerY / viewport.scale - viewport.y / viewport.scale;
  return { x, y };
};

export const getInsertXY = ({ width, height, gap = 20 }) => {
  return findInsertPosition({ width, height }, gap);
};

export const moveAfterInsert = ({ x, y, height: h, gap = 20 }) => {
  const { x: vx, y: vy, scale } = getViewport();
  const left = (296 - vx) / scale;
  const bottom = (document.body.getBoundingClientRect().height - vy) / scale;
  let viewport = null;
  if (x - gap < left) {
    viewport = Object.assign(viewport || {}, {
      x: 296 - (x - gap) * scale,
    });
  }
  if (y + h + gap > bottom) {
    viewport = Object.assign(viewport || {}, {
      y: document.body.getBoundingClientRect().height - (y + h + gap) * scale,
    });
  }
  if (viewport) {
    setViewport(viewport);
  }
};

export const insertGalleryImage = async (galleryItem, focus = false) => {
  const { imageUrl: url, width: w, height: h, negativePrompt, imageUuid } = galleryItem;
  const otherParams = {};

  try {
    const imageData = await getImageParams({ styleImageUuid: imageUuid });

    let generateParams = {};

    try {
      generateParams = JSON.parse(imageData.data?.extraInfo) || {};
    } catch (e) {
      generateParams = {};
    }

    const params = Object.assign(generateParams, generateParams.text2img || generateParams.img2img);

    if (params) {
      const {
        samplingMethod: Sampler,
        samplingStep: Steps,
        cfgScale,
        clipSkip,
        vaeName: vae_name,
        negPrompt,
        negativePrompt: ng,
        negative_prompt,
        prompt: p,
      } = params;

      Object.assign(otherParams, {
        samplerMethod:
          samplerMethodOptions.find((i) => i.label === Sampler)?.value || initialGenerateParams.samplerMethod,
        samplingSteps: Steps,
        cfgScale: cfgScale || initialGenerateParams.cfgScale,
        clipSkip: clipSkip || initialGenerateParams.clipSkip,
        vae:
          vae_name === 'automatic' ? '-1' : vaeOptions.find((i) => vae_name?.indexOf(i.label) > -1)?.value || '22541',
        negativePrompt: negPrompt || negative_prompt || ng || negativePrompt,
        prompt: p,
        width: params.width || imageData.data?.width,
        height: params.height || imageData.data?.height,
      });
    }
    Object.assign(otherParams, {
      checkpoint: imageData?.data?.baseModel,
      loraModels: imageData?.data?.loraModes?.map((item) => ({
        weight: 0.8,
        ...item,
      })),
    });
  } catch (e) {
    console.warn('接口解析pngInfo失败：', e);
  }

  const gap = 20;
  const [x, y] = getInsertXY({ width: w, height: h, gap });
  const id = `element-image-${uniqid()}`;

  insertImage({
    id,
    url,
    x,
    y,
    width: w,
    height: h,
    from: 'gallery',
  });

  if (focus) {
    setTimeout(() => {
      viewportActions.locateElement(id, { animate: true, keepScale: true });
    }, 10);
  }
};

export const insertReporduceSourceImage = async (params) => {
  const { url, width, height, innerTaskId, innerImageId } = params.sourceImage;
  const { x, y } = findNextTaskPosition();

  const image = insertImage({
    url,
    x,
    y,
    width,
    height,
    innerTaskId,
    innerImageId,
    from: 'info-panel',
  });

  const genInfo = await getElementGenInfo(params.sourceImage);

  viewportActions.locateElement(image.id, true);

  await new Promise((resolve) => setTimeout(resolve, 100));
  return image;
};

export const reproduceInpaint = async (params) => {
  // ...
};

export const reproduceOutpaint = async (params) => {
  // ...
};

export const reproduceUpscale = async (params) => {
  // ...
};

export const reproduceCollage = async (params) => {
  // ...
};

export const varyImage = async (id, options) => {
  const elements = getProjectState('elements');
  const element = elements.find((element) => element.id === id);
  const isOverallVary = options?.isOverallVary;
  const count = getUnitData(null, 'count');

  if (!isMainland && vipOptions.includes(count)) {
    const userVipInfo = getVipState('userVipInfo');
    const accountLevel = userVipInfo?.effective ? userVipInfo.accountLevel : 1;
    const isOrdinaryUser = accountLevel === 1;

    if (isOrdinaryUser) {
      updateVipState({
        proPurchaseModalVisible: true,
        currentErrorCode: 1200000148,
        currentErrorMsg: 'You have exceeded the image limit for free users. Please upgrade for unlimited access.',
      });
      return;
    }
  }

  if (!element || element.type !== 'image') return;

  let genInfo = await getElementGenInfo(element);
  if (!genInfo) return;

  genInfo = genInfo || {};

  const generateParams = {
    ...initialGenerateParams,
    ...genInfo,
    controlnets: [],
    imageReference: null,
    isOverallVary: false,
    isDetailsVary: false,
    count: count || 4,
    width: Math.ceil(element.width * element.scaleX),
    height: Math.ceil(element.height * element.scaleY),
    seedNumber: -1,
  };

  if (isOverallVary) {
    // 整体调整，保持模型组合和权重，使用原图做ipa
    generateParams.isOverallVary = true;

    const ipAdapterConfig = controlNetConfig.ip_adapter[genInfo.baseType];

    if (ipAdapterConfig) {
      generateParams.controlnets = [
        {
          id: uniqid(),
          type: 'ip_adapter',
          sourceImage: element,
          previewImage: '',
          controlWeight: 0.6,
          controlMode: 0,
          resizeMode: 1,
          startingControlStep: 0,
          endingControlStep: 0.6,
          pixelPerfect: 1,
          preprocessor: ipAdapterConfig.preprocessors[0],
          model: ipAdapterConfig.models[0],
        },
      ];
    }
  } else {
    // 细节调整
    // 保持模型组合和权重
    // 使用原图做图生图（0.85）
    generateParams.isDetailsVary = true;
    generateParams.imageReference = {
      id: uniqid(),
      url: element.url,
      width: element.width,
      height: element.height,
      strength: 0.15,
    };
  }

  if (generateParams.styleCode && !LoraCodeReg.test(generateParams.prompt)) {
    generateParams.prompt = `#style: ${generateParams.styleCode}, ${generateParams.prompt}`;
  }

  if (generateParams.prompt && generateParams.extendedPrompt) {
    updatePromptCache(generateParams.prompt, generateParams.extendedPrompt);
  }

  console.log(`[DEBUG] varyImage`, generateParams);

  scheduleTask({ type: 'generator', unitId: 'unit-generator-' + uniqid(), params: generateParams });
};

export const insertImages = (images) => {
  const { elements } = getProjectState();
  const nameIndex = getNexIndex(elements, 'Image');

  return insertElements(
    images.map((image, index) => {
      return {
        id: image.id,
        url: image.url,
        x: image.x,
        y: image.y,
        originalX: image.x,
        originalY: image.y,
        type: 'image',
        data: { illegal: image.illegal },
        illegal: image.illegal,
        innerTaskId: image.innerTaskId,
        innerImageId: image.innerImageId,
        name: image.name || 'Image ' + (nameIndex + index - 1),
        width: image.width,
        height: image.height,
        originalUrl: image.url,
      };
    }),
  );
};

export const insertImage = ({ id, url, innerImageId, innerTaskId, x, y, width, height, illegal }) => {
  id = id || `element-image-${uniqid()}`;

  return insertImages([
    {
      id,
      type: 'image',
      url,
      data: { illegal },
      innerImageId,
      innerTaskId,
      x,
      y,
      width,
      height,
      originalX: x,
      originalY: y,
      originalUrl: url,
    },
  ])[0];
};

export const calcShapeArea = (shape) => {
  const firstPoint = shape.points[0];
  const points = shape.points.slice(1);

  let area = 0;

  points.forEach((point, index) => {
    const nextPoint = points[index + 1] || firstPoint;
    area += (point[0] - nextPoint[0]) * (point[1] + nextPoint[1]);
  });

  return Math.abs(area / 2);
};

export const restoreToOriginalImage = (id) => {
  // ...
};

/**
 *
 * @param {*} imagePath
 * @param {*} mode inpaint, changeFace
 * @returns
 */
export const getImageSegmentCacheKey = (imagePath, mode) => {
  return `${imagePath}${mode}`;
};

export const fetchImageSegmentShapes = async (imageId, mode = 'inpaint') => {
  const { elements } = getProjectState();
  const loading = getLoadings(imageId + '-segmenting');

  const isInpainting = mode === 'inpaint';
  // const isChangeFace = mode === 'changeFace';

  if (loading) return;

  const image = elements.find((element) => element.id === imageId);

  if (!image || image.type !== 'image') return;

  // if (image.inpaint?.selections?.length) {
  //   return;
  // }

  try {
    if (!image || image.type !== 'image') return;

    const imagePath = getImageOssPath(image.url);
    const imageSegmentCacheKey = getImageSegmentCacheKey(imagePath, mode);
    const cachedShapes = useSegments.getState()[imageSegmentCacheKey];

    if (cachedShapes) return;

    getLoadings().startLoading(imageId + '-segmenting');

    // 因为image的width/height属性可能不一定是图片真实的尺寸(生图暗策略)
    // 而segment实际上是按照图片真实尺寸来计算的，这里需要先获取图片真实尺寸，然后计算一下比例
    const remoteWidth = await getRemoteImageSize(image.url).catch(() => null);

    if (!remoteWidth) throw new Error(translate('image_segment_error_sub'));

    const scale = image.width / remoteWidth.width;
    const shapes = await requestSegmentImageWithCache(imagePath, isNaN(scale) ? 1 : scale, isInpainting ? 1 : 8);

    if (!shapes || !shapes.length) {
      throw new Error(translate('image_segment_empty'));
    }

    if (Array.isArray(shapes)) {
      useSegments.getState().addItem(imageSegmentCacheKey, shapes);
    }
  } catch (error) {
    notifyError(translate('image_segment_error'), translate('image_segment_error_sub'));
  } finally {
    getLoadings().stopLoading(imageId + '-segmenting');
  }
};

export const calcImageCrop = (width, height, scaleX, scaleY) => {
  const frameWidth = width * scaleX;
  const frameHeight = height * scaleY;
  const frameAspectRatio = Math.abs(frameWidth / frameHeight);
  const imageAspectRatio = Math.abs(width / height);

  let newWidth;
  let newHeight;

  if (frameAspectRatio >= imageAspectRatio) {
    newWidth = width;
    newHeight = width / frameAspectRatio;
  } else {
    newWidth = height * frameAspectRatio;
    newHeight = height;
  }

  return {
    x: (width - newWidth) / 2,
    y: (height - newHeight) / 2,
    width: Math.round(newWidth),
    height: Math.round(newHeight),
    frameAspectRatio,
    imageAspectRatio,
  };
};

export const forceRenameToPng = (name) => {
  if (!name) return name;
  if (name.endsWith('.png')) return name;
  return name + '.png';
};

const upscaleShape =
  (scale) =>
  ([x, y]) => {
    return [x * scale, y * scale];
  };

function extendPolygon(points, distance) {
  function normalize(vector) {
    const length = Math.sqrt(vector[0] * vector[0] + vector[1] * vector[1]);
    return [vector[0] / length, vector[1] / length];
  }

  function perpendicular(vector) {
    // 2D perpendicular vector
    return [-vector[1], vector[0]];
  }

  const expandedPoints = [];
  const len = points.length;

  for (let i = 0; i < len; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % len]; // Next point (wrap around)

    // Edge vector
    const edge = [p2[0] - p1[0], p2[1] - p1[1]];

    // Normalized perpendicular (right-hand normal)
    const normal = normalize(perpendicular(edge));

    // Offset both points by the normal scaled by the distance
    expandedPoints.push([p1[0] + normal[0] * distance, p1[1] + normal[1] * distance]);
  }

  return expandedPoints;
}

export const generateMask = async (element, options = {}) => {
  const {
    hires = false,
    fgColor = '#fff',
    bgColor = '#000',
    type = 'inpaint',
    toTempBucket = true,
    bgTransparent = false,
    fixPoints,
    segments,
  } = options;

  const { selections = [], extend, invert } = segments || element[type] || {};
  const crop = calcImageCrop(element.width, element.height, element.scaleX, element.scaleY);

  const scaleBy = hires ? defaultHiResScale : 1;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = crop.width * scaleBy;
  canvas.height = crop.height * scaleBy;

  ctx.fillStyle = invert ? fgColor : bgColor; //'#fff';
  ctx.fillRect(0, 0, crop.width * scaleBy, crop.height * scaleBy);

  ctx.fillStyle = invert ? bgColor : fgColor; //bgColor;
  ctx.strokeStyle = invert ? bgColor : fgColor; //bgColor;

  const feathering = typeof extend === 'number' ? extend : 0;

  if (feathering) {
    ctx.filter = `blur(${feathering * 2}px)`;
  }

  selections &&
    selections.forEach((selection) => {
      const { type, size } = selection;
      let { points } = selection;

      if (fixPoints) {
        points = points.map((coor) => [coor[0] * element.width, coor[1] * element.height]);
      }

      points = points.map(upscaleShape(scaleBy));

      ctx.lineWidth = typeof extend !== 'number' ? 0 : extend;

      if (type === 'lasso') {
        if (feathering) {
          points = extendPolygon(points, Math.ceil(feathering / 2));
        }

        const firstPoint = points[0];
        ctx.beginPath();
        ctx.moveTo(firstPoint[0] - crop.x, firstPoint[1] - crop.y);
        points.forEach((point) => {
          ctx.lineTo(point[0] - crop.x, point[1] - crop.y);
        });
        ctx.lineTo(firstPoint[0] - crop.x, firstPoint[1] - crop.y);
        ctx.fill();
        ctx.closePath();
        ctx.stroke();
      } else if (type === 'rect') {
        if (feathering) {
          points = extendPolygon(points, feathering);
        }
        ctx.lineJoin = 'miter';
        ctx.lineCap = 'butt';
        ctx.beginPath();
        ctx.moveTo(points[0][0] - crop.x, points[0][1] - crop.y);
        ctx.lineTo(points[1][0] - crop.x, points[0][1] - crop.y);
        ctx.lineTo(points[1][0] - crop.x, points[1][1] - crop.y);
        ctx.lineTo(points[0][0] - crop.x, points[1][1] - crop.y);
        ctx.fill();
        ctx.closePath();
        ctx.stroke();
      } else if (type === 'brush' || type === 'eraser') {
        if (invert) {
          ctx.strokeStyle = type === 'brush' ? bgColor : fgColor;
        } else {
          ctx.strokeStyle = type === 'brush' ? fgColor : bgColor;
        }

        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        ctx.lineWidth = size * scaleBy;
        const firstPoint = points[0];
        ctx.beginPath();
        ctx.moveTo(firstPoint[0] - crop.x, firstPoint[1] - crop.y);

        points.forEach((point) => {
          ctx.lineTo(point[0] - crop.x, point[1] - crop.y);
        });

        // ctx.closePath();
        ctx.stroke();
      }
    });

  if (bgTransparent) {
    // 将所有和背景色一致的像素设置为透明
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  if (feathering) {
    ctx.filter = `blur(0px)`;
  }

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

  if (globalThis.STARFLOW_DEBUG_ENABLED) {
    window.open(URL.createObjectURL(blob));
  }

  const imageFile = new File([blob], forceRenameToPng(element.name), { type: 'image/png' });
  const imageHash = await new Promise((resolve) => calculateHash(imageFile, resolve));

  const cachedUrl = getSharedData('inpainting-mask-' + imageHash);

  if (cachedUrl) {
    console.log('xxx cachedUrl', cachedUrl);
    return cachedUrl;
  }

  const result = await uploadImage(imageFile, toTempBucket);
  console.log('xxx mask');
  if (!result?.url) {
    throw new Error(translate('upload_fail_toast'));
  }

  const url = result.url;
  console.log('xxx maskurl', url);
  setSharedData('inpainting-mask-' + imageHash, url);

  return url;
};
/**
 * 把原图和蒙版混合
 */
export const combineMaskAndCanvas = async (maskCanvas, orginalUrl) => {
  const maskCtx = maskCanvas.getContext('2d');
  let orginalImg;
  if (typeof orginalUrl === 'string') {
    orginalImg = await scheduleLoadImage(orginalUrl);
  } else {
    orginalImg = orginalUrl;
  }
  const orginalImgCanvas = document.createElement('canvas');
  orginalImgCanvas.height = orginalImg.naturalHeight;
  orginalImgCanvas.width = orginalImg.naturalWidth;
  const orginalImgCanvasCtx = orginalImgCanvas.getContext('2d');
  const maskCanvasData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
  orginalImgCanvasCtx.drawImage(orginalImg, 0, 0, orginalImg.naturalWidth, orginalImg.naturalHeight);
  const orginalImgData = orginalImgCanvasCtx.getImageData(0, 0, orginalImg.naturalWidth, orginalImg.naturalHeight);
  const data = maskCanvasData.data;
  for (var i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // const a = data[i+3]
    if (r === 255 || g === 255 || b === 255) {
      orginalImgData.data[i] = 0;
      orginalImgData.data[i + 1] = 0;
      orginalImgData.data[i + 2] = 0;
      orginalImgData.data[i + 3] = 0;
    }
  }
  maskCtx.putImageData(orginalImgData, 0, 0);
};
export const generateMaskOnCanvas = ({
  selections,
  crop,
  extend,
  invert,
  canvas,
  bgTransparent = false,
  fgColor = '#fff',
  bgColor = '#000',
}) => {
  canvas = canvas || document.createElement('canvas');

  const scaleBy = 1;
  const ctx = canvas.getContext('2d');
  canvas.width = crop.width * scaleBy;
  canvas.height = crop.height * scaleBy;

  ctx.fillStyle = invert ? fgColor : bgColor; //'#fff';
  ctx.fillRect(0, 0, crop.width * scaleBy, crop.height * scaleBy);

  ctx.fillStyle = invert ? bgColor : fgColor; //bgColor;
  ctx.strokeStyle = invert ? bgColor : fgColor; //bgColor;

  const feathering = typeof extend === 'number' ? extend : 0;

  if (feathering) {
    ctx.filter = `blur(${feathering * 2}px)`;
  }

  selections &&
    selections.forEach((selection) => {
      const { type, size } = selection;
      let points = selection.points.map(upscaleShape(scaleBy)).map((item) => {
        return [item[0] * canvas.width, item[1] * canvas.height];
      });

      ctx.lineWidth = typeof extend !== 'number' ? 0 : extend;

      if (type === 'lasso') {
        if (feathering) {
          points = extendPolygon(points, Math.ceil(feathering / 2));
        }

        const firstPoint = points[0];
        ctx.beginPath();
        ctx.moveTo(firstPoint[0] - crop.x, firstPoint[1] - crop.y);
        points.forEach((point) => {
          ctx.lineTo(point[0] - crop.x, point[1] - crop.y);
        });
        ctx.lineTo(firstPoint[0] - crop.x, firstPoint[1] - crop.y);
        ctx.fill();
        ctx.closePath();
        ctx.stroke();
      } else if (type === 'rect') {
        if (feathering) {
          points = extendPolygon(points, feathering);
        }

        ctx.beginPath();
        ctx.moveTo(points[0][0] - crop.x, points[0][1] - crop.y);
        ctx.lineTo(points[1][0] - crop.x, points[0][1] - crop.y);
        ctx.lineTo(points[1][0] - crop.x, points[1][1] - crop.y);
        ctx.lineTo(points[0][0] - crop.x, points[1][1] - crop.y);
        ctx.fill();
        ctx.closePath();
        ctx.stroke();
      } else if (type === 'brush' || type === 'eraser') {
        if (invert) {
          ctx.strokeStyle = type === 'brush' ? bgColor : fgColor;
        } else {
          ctx.strokeStyle = type === 'brush' ? fgColor : bgColor;
        }

        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.lineWidth = size * scaleBy;
        const firstPoint = points[0];
        ctx.beginPath();
        ctx.moveTo(firstPoint[0] - crop.x, firstPoint[1] - crop.y);

        points.forEach((point) => {
          ctx.lineTo(point[0] - crop.x, point[1] - crop.y);
        });

        // ctx.closePath();
        ctx.stroke();
      }
    });

  if (bgTransparent) {
    // 将所有和背景色一致的像素设置为透明
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i] === 0 && data[i + 1] === 0 && data[i + 2] === 0) {
        data[i + 3] = 0;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  if (feathering) {
    ctx.filter = `blur(0px)`;
  }
  return canvas;
};
export const cropCanvas = (sourceImage, range) => {
  const { minX, minY, maxX, maxY } = range;
  const newCanvas = document.createElement('canvas');
  const newCtx = newCanvas.getContext('2d');
  const width = maxX - minX;
  const height = maxY - minY;
  newCanvas.width = width;
  newCanvas.height = height;
  newCtx.drawImage(sourceImage, minX, minY, newCanvas.width, newCanvas.height, 0, 0, width, height);
  return newCanvas;
  // downloadCanvas(newCanvas)
};
export const cropTransparentEdge = (sourceImage) => {
  const canvas = document.createElement('canvas');
  canvas.width = sourceImage.naturalWidth;
  canvas.height = sourceImage.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(sourceImage, 0, 0);
  const width = canvas.width;
  const height = canvas.height;
  let [minX, minY, maxX, maxY] = [width, height, 0, 0];
  const { data: imageData } = ctx.getImageData(0, 0, width, height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      const r = imageData[index];
      const g = imageData[index + 1];
      const b = imageData[index + 2];
      const a = imageData[index + 3];

      if (a > 0) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  const newCanvas = document.createElement('canvas');
  const newWidth = maxX - minX;
  const newHeight = maxY - minY;
  newCanvas.width = newWidth;
  newCanvas.height = newHeight;
  const newCtx = newCanvas.getContext('2d');
  newCtx.drawImage(sourceImage, minX, minY, newWidth, newHeight, 0, 0, newWidth, newHeight);
  return newCanvas;
};
export const extractMaskContent = async (maskCanvas, sourceImage) => {
  const maskCtx = maskCanvas.getContext('2d');
  let orginalImg = sourceImage;
  const orginalImgCanvas = document.createElement('canvas');
  orginalImgCanvas.height = orginalImg.naturalHeight;
  orginalImgCanvas.width = orginalImg.naturalWidth;
  const orginalImgCanvasCtx = orginalImgCanvas.getContext('2d');
  const maskCanvasData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
  orginalImgCanvasCtx.drawImage(orginalImg, 0, 0, orginalImg.naturalWidth, orginalImg.naturalHeight);
  const orginalImgData = orginalImgCanvasCtx.getImageData(0, 0, orginalImg.naturalWidth, orginalImg.naturalHeight);
  const data = maskCanvasData.data;
  for (var i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // const a = data[i+3]
    if (!(r === 255 && g === 255 && b === 255)) {
      orginalImgData.data[i] = 0;
      orginalImgData.data[i + 1] = 0;
      orginalImgData.data[i + 2] = 0;
      orginalImgData.data[i + 3] = 0;
    }
  }
  orginalImgCanvasCtx.putImageData(orginalImgData, 0, 0);
  return orginalImgCanvas;
};
export const uploadCanvas = async (canvas) => {
  const data = canvas.toDataURL('image/png');
  const blob = await fetch(data).then((res) => res.blob());
  const file = new File([blob], 'temp.png', { type: 'image/png' });
  const result = await uploadImage(file);
  return result;
};
export const generateAndUploadMask = async (params) => {
  const canvas = generateMaskOnCanvas(params);
  if (params.isCombine) {
    await combineMaskAndCanvas(canvas, params.url);
  }
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

  if (globalThis.STARFLOW_DEBUG_ENABLED) {
    window.open(URL.createObjectURL(blob));
  }

  const imageFile = new File([blob], forceRenameToPng(params.name || `${Date.now()}`), { type: 'image/jpeg' });
  const imageHash = await new Promise((resolve) => calculateHash(imageFile, resolve));

  const cachedUrl = getSharedData('mask-url-' + imageHash);

  if (cachedUrl) return cachedUrl;

  const result = await uploadImage(imageFile, params.toTempBucket);

  if (!result?.url) {
    throw new Error(translate('upload_fail_toast'));
  }

  setSharedData('mask-url-' + imageHash, result.url);
  return result.url;
};

export const getMaskRange = (params) => {
  const canvas = generateMaskOnCanvas(params);
  const ctx = canvas.getContext('2d');
  // 遍历数据
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  let [minX, minY, maxX, maxY] = [width, height, 0, 0];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      const r = data[index];
      const g = data[index + 1];
      const b = data[index + 2];
      // const a = data[index + 3];
      // console.log(`坐标为 (${x}, ${y}) 的像素颜色为：RGB(${r}, ${g}, ${b})，透明度为：${a}`);
      if (r === 255 || g === 255 || b === 255) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }
  return { minX, minY, maxX, maxY, maskCanvas: canvas };
};

export const saveImageToBestPosition = (data) => {
  const [x, y] = findInsertPosition({ width: data.width, height: data.height }, 20);

  const id = insertImage({
    x,
    y,
    url: data.url,
    innerTaskId: data.taskId,
    innerImageId: data.id,
    width: Math.floor(data.width),
    height: Math.floor(data.height),
  });

  viewportActions.locateElement(id, { animate: true, keepScale: true });
};

export const convertGroupToImage = async (groupId) => {
  const { scale } = getViewport();
  const { startLoading, stopLoading } = getLoadings();
  const elements = getProjectState('elements');
  const targetGroup = elements.find((element) => element.id === groupId);
  const isMerging = getLoadings(groupId + '-merging');

  if (isMerging || !targetGroup || targetGroup.type !== 'group') return;

  const { x, y } = targetGroup;

  startLoading(groupId + '-merging');

  try {
    // todos fqy 这里如果需要旋转目前还不支持自己绘制
    const konvaStage = getSharedStage();
    const groupNode = konvaStage.findOne(`#${groupId}`);
    const blob = await groupNode.toBlob({ pixelRatio: 1 / scale });

    // const groupCanvas = await convertGroupToCanvas(targetGroup);
    // const base64Url = groupCanvas.toDataURL('image/png');
    // const blob = await fetch(base64Url).then((res) => res.blob());

    const originalFile = new File([blob], forceRenameToPng(getNextName(elements, 'Image')), { type: 'image/png' });
    const { file, compressed } = await compressLocalImage(originalFile, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE);

    if (compressed) {
      notifyWarn(translate('img_resize_toast'));
    }

    const name = getNextName(elements, 'Merged Image');

    // 插入新图片到原始位置
    const offset = 30;
    const viewport = getViewport();
    const offsetImage = offset / viewport.scale;
    await insertLocalImage(file, { name, position: { x: x + offsetImage, y: y + offsetImage }, compress: false });
    notifySuccess(translate('image_merge_success'));
  } catch (error) {
    notifyError(translate('image_merge_error'), error.message);
  } finally {
    stopLoading(groupId + '-merging');
  }
};
// todos fqy 目前只完成了图片的
export const convertGroupToCanvas = async ({ width, height, elements, range }) => {
  // range 在用作图生图那三个下拉会有值，其他情况是undefined
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const viewport = getViewport();
  // const maintrans = getSharedStage().findOne('#mainTransformer')
  // console.log(maintrans.getClientRect())
  canvas.width = width;
  canvas.height = height;
  for (let element of elements) {
    ctx.save();
    const elementType = element.type;
    switch (elementType) {
      case 'image':
        const url = element.originalUrl;
        const image = await scheduleLoadImage(url);
        ctx.drawImage(
          image,
          0,
          0,
          element.width,
          element.height,
          element.x - (range?.minX || 0),
          element.y - (range?.minY || 0),
          element.width * element.scaleX,
          element.height * element.scaleY,
        );
        ctx.restore();
        break;
      case 'shape_square':
        break;
      case 'text':
        break;
      case 'shape_circle':
        console.log('xxx shape_circle');
        const data = element.data;
        const { x, y, scaleX, scaleY, width, height, rotation } = element;
        const { scale } = viewport;
        const [centerX, centerY] = [x + (width * scaleX) / 2, y + (height * scaleY) / 2];
        const ellipseX = centerX - (range?.minX || 0); // 椭圆（压扁的圆）的中心x坐标
        const ellipseY = centerY - (range?.minY || 0); // 椭圆（压扁的圆）的中心y坐标
        const ellipseRadiusX = (width * scaleX) / 2; // 椭圆水平方向半径
        const ellipseRadiusY = (height * scaleY) / 2; // 椭圆垂直方向半径
        // ctx.fillStyle = data.backgroundColor;
        ctx.fillStyle = 'red';
        ctx.translate(ellipseX, ellipseY);
        ctx.rotate((rotation / 180) * Math.PI);
        ctx.beginPath();
        ctx.ellipse(0, 0, ellipseRadiusX, ellipseRadiusY, 0, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();
        break;
    }
    ctx.save();
  }
  return canvas;
};
// todos fqy 目前只完成了图片的
export const canvasToImage = (canvas) => {
  return new Promise((resolve) => {
    const data = canvas.toDataURL('image/png');
    var Image = new globalThis.Image();
    Image.src = data;
    Image.onload = () => {
      resolve(Image);
    };
  });
};

export const insertLocalImages = async (files, options) => {
  let uploadedCount = 0;
  let errorCount = 0;
  let totalCount = files.length;

  while (files.length) {
    const localFile = files.shift();

    try {
      await insertLocalImage(localFile, { position: 'same-as-gallery', ...options });
      uploadedCount += 1;
    } catch (error) {
      errorCount += 1;
    }
  }

  return { totalCount, uploadedCount, errorCount };
};

export const insertLocalImage = async (originalFile, options) => {
  if (!ACCEPTED_FILE_TYPES.includes(originalFile.type)) {
    throw new Error(translate('img_format_toast'));
  }

  const { position = 'same-as-gallery', name, compress = true, fitSize, from } = options || {};
  const maxSize = compress ? MAX_UPLOAD_SIZE : Infinity;

  const { file, width, height, compressed } = await compressLocalImage(originalFile, maxSize, maxSize);
  const result = await uploadImage(file, false, from);

  if (!result?.url) {
    throw new Error(translate('upload_fail_toast'));
  }

  if (compress && compressed) {
    notifyWarn(translate('img_resize_toast'));
  }

  let scaleX = 1;
  let scaleY = 1;

  if (fitSize) {
    const fitResult = fitImageSize(width, height, fitSize[0], fitSize[1]);
    scaleX = fitResult.width / width;
    scaleY = fitResult.height / height;
  }

  const renderedWidth = width * scaleX;
  const renderedHeight = height * scaleY;

  const url = result.url;
  const elements = getProjectState('elements');

  let x, y;
  let shouldFixPosition = true;

  if (position === 'same-as-gallery') {
    [x, y] = getInsertXY({ width: renderedWidth, height: renderedHeight });
    moveAfterInsert({ x, y, height: renderedHeight });
    shouldFixPosition = false;
  } else if (typeof position.x === 'number' && typeof position.y === 'number') {
    // 插入到指定位置
    x = position.x;
    y = position.y;
    shouldFixPosition = false;
  } else {
    // 插入到画布中央
    const viewport = getViewport();
    const { width: canvasWidth, height: canvasHeight } = document.body.getBoundingClientRect();
    const centerX = canvasWidth / 2 - (renderedWidth * viewport.scale) / 2;
    const centerY = canvasHeight / 2 - (renderedHeight * viewport.scale) / 2;
    x = centerX / viewport.scale - viewport.x / viewport.scale;
    y = centerY / viewport.scale - viewport.y / viewport.scale;
  }

  if (shouldFixPosition) {
    while (elements.some((element) => element.x === x && element.y === y)) {
      x += 30;
      y += 30;
    }

    // const coveredByGenerator =
    //   x < generators[0].x + generators[0].params.width + 120 &&
    //   x + width > generators[0].x &&
    //   y < generators[0].y + generators[0].params.height &&
    //   y + height > generators[0].y;

    // if (coveredByGenerator) {
    //   x = generators[0].x + generators[0].params.width + 120;
    // }
  }

  return insertImage({ name, url, x, y, width, height, scaleX, scaleY });
};

export const uploadImage = async (file, toTempBucket = false, from) => {
  const { data: userInfo } = await getUserinfo();

  const uploadId = uniqid();

  !!from && mc('tool.image.upload', { upload_id: uploadId, size: file.size, from });

  try {
    const temp = await (toTempBucket ? cloudStorageExts.singleUploadForSDTmp : cloudStorageExts.singleUploadForPro)(
      userInfo.uuid,
      file,
    );

    // 接入机审
    if (isMainland) {
      const res = await imageReview(temp.url);
      if (res.data) {
        notifyError('图片违规，请上传其他图片');
        return null;
        // throw new Error('图片违规，请上传其他图片');
      } else {
        !!from && mc('tool.image.upload.end', { upload_id: uploadId, status: 'succ' });
        return temp;
      }
    } else {
      !!from && mc('tool.image.upload.end', { upload_id: uploadId, status: 'succ' });
      return temp;
    }
  } catch (error) {
    !!from && mc('tool.image.upload.end', { upload_id: uploadId, status: 'fail' });
    notifyError(translate('upload_fail_toast'));
    throw error;
  }
};

const remoteCompressCache = {};

export async function compressRemoteImage(url, maxWidth, maxHeight, uploadToTemp = false) {
  const cacheId = `${maxWidth}-${maxHeight}-${url}-${uploadToTemp ? '1' : '0'}`;

  if (remoteCompressCache[cacheId]) {
    return remoteCompressCache[cacheId];
  }

  const base64 = await convertRemoteImageToBase64(url, maxWidth, maxHeight, 'image/png');
  const blob = await fetch(base64).then((res) => res.blob());
  const file = new File([blob], 'temp.png', { type: 'image/png' });
  const uploadRes = await uploadImage(file, uploadToTemp);

  remoteCompressCache[cacheId] = uploadRes.url;

  return uploadRes.url;
}

export const compressWithSuffix = (url) => convert2CompressedUrl(url, 200);

export const copyOSSImage = async (url, toTemp, bizCode, destPath) => {
  url = getImageOssUrl(url);

  const cacheIndicate = 'copy:' + url + ':' + (toTemp ? '1' : '0');

  if (getSharedData(cacheIndicate)) {
    return getSharedData(cacheIndicate);
  }

  const result = await (toTemp
    ? cloudStorageExts.copyObjectToTmp(url, bizCode, destPath)
    : cloudStorageExts.copyObjectToOnline(url, bizCode, destPath));

  if (!result?.url) {
    throw new Error(translate('upload_fail_toast'));
  }

  setSharedData(cacheIndicate, result);
  return result;
};

export const downloadImage = (url, stripMeta = true) =>
  new Promise((resolve) => {
    const filename = url.split('/').pop();
    const link = document.createElement('a');
    if (!stripMeta) {
      link.href = url + '?attname=' + filename;
      link.download = filename;
      link.click();
    } else {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const image = new Image();

      image.src = url;
      image.crossOrigin = 'anonymous';

      image.onload = function () {
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(image, 0, 0);
        const blob = canvas.toDataURL('image/png');
        link.href = blob;
        link.download = filename;
        link.click();
        resolve();
      };
    }
  });

const cachedImageStyles = {};

export const fetchImageStyle = async (element) => {
  if (cachedImageStyles[element.url]) {
    return cachedImageStyles[element.url];
  }

  const result = await requestRevStyle(getImageOssPath(element.url)).then((res) => res.response?.[0]);

  if (result?.description) {
    result.description = await tryTranslatePrompt(result.description);
  }

  if (result && result.substyle) {
    if (result) {
      cachedImageStyles[element.url] = result;
    }
    return result;
  }

  return null;
};

const cachedPrompts = {};

export const tryTranslatePrompt = async (prompt) => {
  const promptLang = getTempState('promptLang');

  if (promptLang !== 'zh') {
    return prompt;
  }

  try {
    const res = await submitTranslate({
      q: prompt,
      to: 'zh',
      from: 'auto',
    });

    return res.data?.dst || prompt;
  } catch (error) {
    console.error('tryTranslatePrompt -> error', error);
    return prompt;
  }
};

export const fetchPrompt = async (element) => {
  let resultText = null;

  const genInfo = await getElementGenInfo(element);

  if (genInfo?.prompt) {
    resultText = genInfo.prompt;
  } else {
    const { startLoading, stopLoading } = getLoadings();

    if (!element) return null;

    try {
      startLoading('fetch-prompt-' + element.id);

      if (cachedPrompts[element.url]) {
        return cachedPrompts[element.url];
      }

      resultText = await requestRevPromptV2(getImageOssPath(element.url));
      // resultText = await requestRevPrompt({
      //   images: [getImageOssPath(element.url)],
      //   ...inferenceAPIBucket,
      // }).then((result) => result.results?.[0]?.caption);

      if (!resultText) {
        notifyError(translate('prompt_fetch_error'), translate('prompt_fetch_empty'));
        return null;
      }

      cachedPrompts[element.url] = resultText;
    } catch (error) {
      notifyError(translate('prompt_fetch_error'), error.message || error.msg || error);
      return null;
    } finally {
      stopLoading('fetch-prompt-' + element.id);
    }
  }

  return await tryTranslatePrompt(resultText);
};

const cachedImages = {};
const imageLoadQueue = {};

export const scheduleLoadImage = async (url) => {
  if (cachedImages[url]) {
    return cachedImages[url];
  }

  if (imageLoadQueue[url]) {
    return imageLoadQueue[url];
  }

  const promise = new Promise((resolve, reject) => {
    const image = new Image();

    image.src = url;
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      cachedImages[url] = image;
      delete imageLoadQueue[url];
      resolve(image);
    };

    image.onerror = (error) => {
      delete imageLoadQueue[url];
      reject(error);
    };
  });

  imageLoadQueue[url] = promise;

  return promise;
};

export const copyKonvaImageToClipboard = async (elementId) => {
  try {
    const konvaImage = getSharedStage().findOne(`#${elementId}-image`);

    if (!konvaImage || !konvaImage.attrs?.image) {
      notifyError(translate('copy_image_error'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = konvaImage.attrs.width;
    canvas.height = konvaImage.attrs.height;
    ctx.drawImage(konvaImage.attrs?.image, 0, 0);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    const item = new ClipboardItem({ 'image/png': blob });

    await navigator.clipboard.write([item]);

    notifySuccess(translate('copy_image_toast'));
  } catch (error) {
    notifyError(translate('copy_image_error'));
    console.error('copyKonvaImageToClipboard -> error', error);
  }
};

export const copyImageToClipboard = async (url) => {
  try {
    const image = await scheduleLoadImage(url);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    const item = new ClipboardItem({ 'image/png': blob });

    await navigator.clipboard.write([item]);

    notifySuccess(translate('copy_image_toast'));
  } catch (error) {
    notifyError(translate('copy_image_error'));
    console.error('copyImageToClipboard -> error', error);
  }
};

export const segmentWorkflowImage = async (imageUrl) => {
  try {
    const imagePath = getImageOssPath(imageUrl);
    const cacheKey = getImageSegmentCacheKey(imagePath, 'workflow');
    const cachedShapes = useSegments.getState()[cacheKey];

    if (cachedShapes) return;

    const shapes = await requestSegmentImageWithCache(imagePath, 1, 1, false);

    if (!shapes || !shapes.length) {
      throw new Error(translate('image_segment_empty'));
    }

    if (Array.isArray(shapes)) {
      useSegments.getState().addItem(cacheKey, shapes);
    }
  } catch (error) {
    notifyError(translate('image_segment_error'), translate('image_segment_error_sub'));
  }
};
